import { Txt, TxtProps } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { GlobalStyles, theme } from 'lib/theme';
import React from 'react';
import {
  GestureResponderEvent,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import reactStringReplace from 'react-string-replace';
import { Button, ButtonTheme } from './Button';
import { Icon, ICONS } from './Icon';
import { TxtAsterisk } from './TxtAsterisk';

export const CardBorderRadius = 8;

export type CardProps = {
  label?: string;
  required?: boolean;
  invalid?: boolean;
  labelIcon?: ICONS;
  labelSize?: TxtProps['size'];
  labelStyle?: StyleProp<ViewStyle>;
  margin?: boolean;
  marginBottomHalf?: boolean;
  marginBottomQuarter?: boolean;
  marginTop?: boolean;
  marginHorizontal?: boolean;
  explanation?: string | React.ReactNode;
  explanationIcon?: ICONS;
  explanationLinkText?: string;
  explanationLinkAccessibilityLabel?: string;
  explanationLinkAccessibilityHint?: string;
  explanationLinkIcon?: ICONS;
  onExplanationLinkPress?: () => void;
  labelLinkText?: string;
  onLabelLinkPress?: ((event: GestureResponderEvent) => void) | null;
  labelLinkText2?: string;
  onLabelLinkPress2?: ((event: GestureResponderEvent) => void) | null;
  separator?: React.ReactNode | null;
  style?: StyleProp<ViewStyle>;
  bodyStyle?: StyleProp<ViewStyle>;
  expanded?: boolean;
  expandedInline?: boolean;
  expandedInlineFirst?: boolean;
  expandedInlineLast?: boolean;
  children?: React.ReactNode | React.ReactNode[];
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  withShadow?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  contentHorizontal?: boolean;
};

export const Card: React.FC<CardProps> = ({
  bodyStyle,
  children,
  explanation,
  explanationIcon,
  explanationLinkText,
  explanationLinkAccessibilityLabel,
  explanationLinkAccessibilityHint,
  explanationLinkIcon,
  onExplanationLinkPress,
  label,
  required,
  labelIcon,
  labelLinkText,
  labelStyle,
  onLabelLinkPress,
  labelLinkText2,
  onLabelLinkPress2,
  margin = true,
  marginBottomHalf = false,
  marginBottomQuarter = false,
  marginTop = false,
  marginHorizontal = true,
  separator,
  style,
  expanded,
  expandedInline,
  expandedInlineFirst,
  expandedInlineLast,
  labelSize = 'small',
  borderRadiusTop = true,
  borderRadiusBottom = true,
  withShadow = true,
  contentStyle,
  contentHorizontal = false,
  invalid,
}) => (
  <View
    style={[
      marginBottomHalf
        ? styles.viewWithMarginBottomHalf
        : marginBottomQuarter
          ? styles.viewWithMarginBottomQuarter
          : margin
            ? styles.viewWithMarginBottom
            : undefined,
      marginTop ? styles.viewWithMarginTop : undefined,
      expandedInline
        ? { marginTop: -5, marginBottom: -5, zIndex: 1 }
        : undefined,
      expandedInlineLast ? { marginBottom: 0 } : undefined,
      expandedInlineFirst ? { marginTop: 0 } : undefined,
      style,
    ]}
  >
    {(!!label || onLabelLinkPress || onLabelLinkPress2 !== undefined) && (
      <View
        style={[
          styles.labelContainer,
          marginHorizontal
            ? { marginHorizontal: theme.paddingHorizontal * 2 }
            : { marginHorizontal: theme.paddingHorizontal },
          labelStyle,
        ]}
      >
        {!!label && (
          <>
            {labelIcon && (
              <Icon
                source={labelIcon}
                style={{ marginRight: theme.paddingHorizontal }}
              />
            )}
            <Txt weight="medium" size={labelSize} style={styles.labelText}>
              {label}
              {required && <TxtAsterisk />}
            </Txt>
          </>
        )}
        {(onLabelLinkPress !== undefined ||
          onLabelLinkPress2 !== undefined) && (
          <ViewWithSeparators
            horizontal
            style={{ marginRight: -(theme.paddingHorizontal / 2) }}
          >
            {onLabelLinkPress !== undefined && (
              <Button
                uppercase={false}
                text={labelLinkText || 'No Text'}
                buttonTheme={ButtonTheme.link}
                compact
                onPress={onLabelLinkPress}
                textWeight="bold"
                textSize="small"
                style={styles.labelLink}
              />
            )}
            {onLabelLinkPress2 !== undefined && (
              <Button
                uppercase={false}
                text={labelLinkText2 || 'No Text'}
                buttonTheme={ButtonTheme.link}
                compact
                onPress={onLabelLinkPress2}
                textWeight="bold"
                textSize="small"
                style={styles.labelLink}
              />
            )}
          </ViewWithSeparators>
        )}
      </View>
    )}
    <View
      style={[
        styles.bodyContainer,
        withShadow ? styles.bodyShadow : undefined,
        borderRadiusTop ? styles.bodyBorderRadiusTop : undefined,
        borderRadiusBottom ? styles.bodyBorderRadiusBottom : undefined,
        marginHorizontal
          ? { marginHorizontal: theme.paddingHorizontal }
          : undefined,
      ]}
    >
      <View
        style={[
          styles.body,
          expanded || expandedInline
            ? {
                borderWidth: 1,
                borderColor: theme.colors.blue80,
              }
            : undefined,
          borderRadiusTop ? styles.bodyBorderRadiusTop : undefined,
          borderRadiusBottom ? styles.bodyBorderRadiusBottom : undefined,
          invalid ? GlobalStyles.invalidFieldOutline : undefined,
          bodyStyle,
        ]}
      >
        {separator !== null ? (
          <ViewWithSeparators
            separator={separator}
            style={contentStyle}
            horizontal={!!contentHorizontal}
          >
            {children}
          </ViewWithSeparators>
        ) : (
          children
        )}
      </View>
    </View>
    {!!explanation && (
      <View
        style={{
          paddingHorizontal: theme.paddingHorizontal * 2,
          paddingTop: 6,
          flexDirection: 'row',
        }}
      >
        {!!explanationIcon && (
          <Icon
            source={explanationIcon}
            size={11}
            style={{ marginTop: 3, marginRight: theme.paddingHorizontal / 2 }}
          />
        )}
        <Txt size="xsmall" style={styles.explanationText}>
          {typeof explanation === 'string'
            ? reactStringReplace(explanation, /\*(.*?)\*/gm, (match, i) => (
                <Txt
                  key={i}
                  size="xsmall"
                  style={styles.explanationText}
                  weight="bold"
                >
                  {match}
                </Txt>
              ))
            : explanation}
        </Txt>
      </View>
    )}
    {!!explanationLinkText && onExplanationLinkPress && (
      <Button
        onPress={onExplanationLinkPress}
        icon={explanationLinkIcon}
        accessibilityHint={explanationLinkAccessibilityHint}
        accessibilityLabel={explanationLinkAccessibilityLabel}
        style={styles.explanationButton}
        textSize="xsmall"
        textWeight="regular"
        textStyle={{ textAlign: 'left', flex: 1 }}
        buttonTheme={ButtonTheme.link}
        numberOfLines={1}
        iconSize={11}
      >
        {explanationLinkText}
      </Button>
    )}
  </View>
);

const styles = StyleSheet.create({
  bodyShadow: {
    shadowColor: 'black',
    shadowOpacity: 0.02,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 2,
  },
  bodyContainer: {
    overflow: 'visible',
  },
  body: {
    overflow: 'hidden',
  },
  bodyBorderRadiusTop: {
    borderTopLeftRadius: CardBorderRadius,
    borderTopRightRadius: CardBorderRadius,
  },
  bodyBorderRadiusBottom: {
    borderBottomLeftRadius: CardBorderRadius,
    borderBottomRightRadius: CardBorderRadius,
  },
  explanationText: {
    color: theme.colors.textBlack64,
    marginVertical: 0,
    paddingBottom: 0,
  },
  explanationButton: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: 6 - 1,
    marginBottom: 0,
    marginLeft: theme.paddingHorizontal * 2 - 6,
    marginRight: theme.paddingHorizontal * 2 - 6,
    borderWidth: 0,
    height: undefined,
  },
  labelContainer: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'center',
  },
  labelLink: {
    marginHorizontal: 0,
    marginVertical: -3,
  },
  labelText: {
    flex: 1,
  },
  viewWithMarginBottom: {
    marginBottom: theme.paddingVertical,
  },
  viewWithMarginBottomHalf: {
    marginBottom: theme.paddingVertical / 2,
  },
  viewWithMarginBottomQuarter: {
    marginBottom: theme.paddingVertical / 4,
  },
  viewWithMarginTop: {
    marginTop: theme.paddingVertical,
  },
});
